import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTour } from '@reactour/tour';
import { useFormikContext } from 'formik';
import useFormWarning from 'hooks/useFormWarning';
import { isEqual } from 'lodash';
import { Button } from 'ui/components/common';
import { Tabs } from 'ui/components/nav';
import { TOUR_SELECTOR } from 'ui/partials/Tour';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import { useGetStages } from '../hooks';
import DealCustomFieldsForm from './DealCustomFieldsForm';
import DealInfoForm from './DealInfoForm';
import { DEAL_FORM } from './schema';

interface PROPS {
  isLoading: boolean;
  setForm: Dispatch<SetStateAction<any>>;
  resetForm: () => void;
}

type TAB_VALUE = 'deal-info' | 'custom-fields';

const TabItems: { label: string; value: TAB_VALUE }[] = [
  { label: 'Deal Info', value: 'deal-info' },
  { label: 'Custom Fields', value: 'custom-fields' },
];

const NewDealForm: FC<PROPS> = ({ isLoading, setForm, resetForm }) => {
  const [selectedTab, setSelectedTab] = useState<TAB_VALUE>('deal-info');
  const { values, handleSubmit, isSubmitting, setFieldValue, initialValues } =
    useFormikContext<DEAL_FORM>();

  const { onBack } = useFormWarning({
    isDirty: !isEqual({ ...initialValues, stageId: values.stageId }, values),
    item: 'deal',
  });

  const renderComponent = useMemo(() => {
    switch (selectedTab) {
      case 'deal-info':
        return <DealInfoForm setForm={setForm} />;
      case 'custom-fields':
        return <DealCustomFieldsForm />;
    }
  }, [selectedTab, setForm]);

  const { isOpen } = useTour();

  useEffect(() => {
    if (isOpen) {
      setFieldValue('name', 'Test Deal');
      setFieldValue('amount', 2000);
      setFieldValue('type', 'NEW_BUSINESS');
    }
  }, [isOpen, setFieldValue]);

  const { data: stages } = useGetStages(values.pipelineId);

  useEffect(() => {
    if (stages?.length && !values.stageId) {
      setFieldValue('stageId', stages[0]?._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  if (!values.stageId) {
    return <div />;
  }

  return (
    <form
      onSubmit={(e) => {
        handleSubmit(e);
      }}
    >
      <div data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.FORM.FORM}>
        <Tabs
          items={TabItems}
          onChange={(val) => setSelectedTab(val.value as typeof selectedTab)}
          selectedTab={selectedTab}
          size={SIZE.Small}
          type="secondary"
        />
        {renderComponent}
      </div>
      <div
        className={cn(
          'pt-[22px] mt-[22px]',
          'flex justify-end gap-2',
          'border-t-[0.5px] border-separator-primary',
        )}
      >
        <Button
          onClick={() => {
            if (resetForm) {
              resetForm();
              onBack();
            } else {
              setForm?.(null);
              onBack();
            }
          }}
          label="CANCEL"
          disabled={isSubmitting}
          intent="tertiary"
          size="sm"
        />
        <Button
          data-tut={TOUR_SELECTOR.TOUR.SALES.DEAL.FORM.SAVE_BUTTON}
          type="submit"
          label="SAVE"
          size="sm"
          disabled={isSubmitting || isLoading}
        />
      </div>
    </form>
  );
};

export default NewDealForm;
